import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionHead, Marquee } from "@/components/site/Widgets";
import { Universities, ProgramExplorer, FAQ, Contact } from "@/pages/Home";
import { api, track, WHATSAPP_URL } from "@/lib/api";

export function useMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", description);
    return () => {
      document.title = "Harwell Global Education — Study in Europe & Romania";
    };
  }, [title, description]);
}

export const PAGES = {
  europe: {
    path: "/study-in-europe",
    slug: "study-in-europe",
    metaTitle: "Study in Europe from India | Bachelor's & Master's | Harwell Global Education",
    metaDesc: "Study in Europe from India with Harwell Global Education. English-taught Bachelor's and Master's programs, affordable European universities and complete application guidance.",
    overline: "European Study Destinations",
    h1a: "STUDY IN",
    h1b: "EUROPE.",
    sub: "English-taught Bachelor's and Master's programs across Europe — affordable, credible and fully guided, with Romania as our flagship destination.",
    image: "https://images.unsplash.com/photo-1577985043696-8bd54d9f093f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMHVuaXZlcnNpdHklMjBjYW1wdXMlMjBldXJvcGV8ZW58MHx8fHwxNzg3OTkzMDkzfDA&ixlib=rb-4.1.0&q=85",
    introTitle: "A European degree is closer than you think",
    intro: [
      "Europe offers international students a compelling alternative to traditional destinations: established universities, English-taught programs, multicultural campuses and tuition that often costs a fraction of the UK, USA or Canada.",
      "Harwell Global Education guides Indian students and families through every step — from profile assessment and university shortlisting to applications, visa guidance and pre-departure preparation.",
    ],
    bullets: [
      "English-taught Bachelor's & Master's programs",
      "European academic environment (ECTS framework)",
      "Affordable tuition and living costs in many countries",
      "Medicine, Engineering, IT, Business, Sciences & more",
      "End-to-end application and visa guidance",
    ],
    programFilter: null,
    programsTitle: "PROGRAMS ACROSS EUROPE",
    faqKeywords: ["Europe", "English-taught", "IELTS", "cost"],
    showUniversities: false,
  },
  romaniaIndians: {
    path: "/study-in-romania-for-indian-students",
    slug: "romania-for-indian-students",
    metaTitle: "Study in Romania for Indian Students | Harwell Global Education",
    metaDesc: "Study in Romania for Indian students — English-taught Bachelor's and Master's programs, practical costs, and complete guidance from Harwell Global Education, Noida.",
    overline: "For Indian Students",
    h1a: "STUDY IN ROMANIA",
    h1b: "FOR INDIAN STUDENTS.",
    sub: "A European Union education designed around Indian academic profiles — from Class 12 to Master's, with guidance from our Noida counselling team.",
    image: "https://images.unsplash.com/photo-1695314620864-b551fd6574ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxyb21hbmlhJTIwYnVjaGFyZXN0JTIwYXJjaGl0ZWN0dXJlJTIwZXVyb3BlfGVufDB8fHx8MTc4Nzk5MzA5M3ww&ixlib=rb-4.1.0&q=85",
    introTitle: "Why Indian students are choosing Romania",
    intro: [
      "Romania combines European Union academics with costs that work for Indian families. Many universities offer English-taught programs, and several accept a Medium of Instruction certificate instead of IELTS — requirements we confirm for every shortlisted program.",
      "Indian student communities are growing across Bucharest, Cluj-Napoca, Iași and Timișoara, and Harwell's counsellors prepare you for documentation, the visa process and life on campus before you fly.",
    ],
    bullets: [
      "Programs matched to Class 12 and Indian degree credentials",
      "IELTS alternatives accepted by some universities (MOI)",
      "Indicative tuition: ~€2,000–€4,000/yr for most programs",
      "Medicine ~€6,000–€8,000/yr (confirm with university)",
      "Pre-departure and accommodation guidance included",
    ],
    programFilter: null,
    programsTitle: "POPULAR PROGRAMS FOR INDIAN STUDENTS",
    faqKeywords: ["Romania", "IELTS", "cost", "documents"],
    showUniversities: true,
  },
  bachelors: {
    path: "/bachelors-in-romania",
    slug: "bachelors-in-romania",
    metaTitle: "Bachelor's in Romania for Indian Students | Harwell Global Education",
    metaDesc: "Study a Bachelor's in Romania after Class 12. English-taught undergraduate programs in Engineering, Computer Science, Business and more — guided by Harwell Global Education.",
    overline: "Undergraduate Pathways",
    h1a: "BACHELOR'S IN",
    h1b: "ROMANIA.",
    sub: "Completed Class 12? Explore English-taught undergraduate programs across Europe's most affordable study destination.",
    image: "https://images.unsplash.com/photo-1689655451590-be38de8003e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxyb21hbmlhJTIwYnVjaGFyZXN0JTIwYXJjaGl0ZWN0dXJlJTIwZXVyb3BlfGVufDB8fHx8MTc4Nzk5MzA5M3ww&ixlib=rb-4.1.0&q=85",
    introTitle: "Your undergraduate years, in the heart of Europe",
    intro: [
      "Romanian universities offer 3 to 4-year Bachelor's programs across Computer Science, Engineering, Business, Life Sciences, International Relations and more — many taught fully in English for international students.",
      "Entry is based on your Class 12 academic record. Harwell assesses your profile, shortlists suitable universities and manages the application, documentation and visa guidance end to end.",
    ],
    bullets: [
      "Open to Class 12 graduates from all streams",
      "3–4 year English-taught programs",
      "Indicative tuition ~€2,000–€4,000 per year",
      "European ECTS credit framework",
      "Safe, affordable student cities",
    ],
    programFilter: { degree: "Bachelor's" },
    programsTitle: "BACHELOR'S PROGRAMS YOU CAN EXPLORE",
    faqKeywords: ["Bachelor's", "requirements", "IELTS", "cost"],
    showUniversities: true,
  },
  masters: {
    path: "/masters-in-romania",
    slug: "masters-in-romania",
    metaTitle: "Master's in Romania for International Students | Harwell Global Education",
    metaDesc: "Pursue a Master's in Romania — English-taught MSc, MA and MBA-style programs for Indian graduates. Affordable tuition, European degrees, full guidance from Harwell Global Education.",
    overline: "Postgraduate Pathways",
    h1a: "MASTER'S IN",
    h1b: "ROMANIA.",
    sub: "For graduates planning their next academic step — English-taught Master's programs aligned with your career goals.",
    image: "https://images.unsplash.com/photo-1627556704263-b486db44a463?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxzdHVkZW50cyUyMHVuaXZlcnNpdHklMjBjYW1wdXMlMjBldXJvcGV8ZW58MHx8fHwxNzg3OTkzMDkzfDA&ixlib=rb-4.1.0&q=85",
    introTitle: "A European Master's without the overwhelming price tag",
    intro: [
      "Romanian universities offer 1.5 to 2-year Master's programs across Data Science, Engineering, Finance, International Relations, Biotechnology and more — within the European ECTS framework.",
      "Harwell maps your Bachelor's background to the right programs, prepares your application dossier and guides you through admission, visa and pre-departure steps.",
    ],
    bullets: [
      "For recognised 3 or 4-year Bachelor's degrees",
      "1.5–2 year English-taught programs",
      "Indicative tuition ~€2,200–€4,200 per year",
      "Career-aligned specialisations",
      "Application, visa and pre-departure support",
    ],
    programFilter: { degree: "Master's" },
    programsTitle: "MASTER'S PROGRAMS YOU CAN EXPLORE",
    faqKeywords: ["Master's", "requirements", "documents", "early"],
    showUniversities: true,
  },
  medicine: {
    path: "/medicine-in-romania",
    slug: "medicine-in-romania",
    metaTitle: "Study Medicine in Romania | English-Taught MD Programs | Harwell Global Education",
    metaDesc: "Study Medicine in Romania in English — General Medicine, Dentistry and Pharmacy at established European universities. Profile assessment and full application guidance from Harwell.",
    overline: "Medical Education",
    h1a: "STUDY MEDICINE IN",
    h1b: "ROMANIA.",
    sub: "English-taught General Medicine, Dentistry and Pharmacy programs at established European medical universities.",
    image: "https://images.unsplash.com/photo-1695314620864-b551fd6574ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxyb21hbmlhJTIwYnVjaGFyZXN0JTIwYXJjaGl0ZWN0dXJlJTIwZXVyb3BlfGVufDB8fHx8MTc4Nzk5MzA5M3ww&ixlib=rb-4.1.0&q=85",
    introTitle: "European medical education, English-taught",
    intro: [
      "Romania has a long tradition of medical education, with universities such as Carol Davila University of Medicine and Pharmacy in Bucharest offering English-taught pathways for international students. General Medicine is typically a 6-year program.",
      "Admission is generally based on your Class 12 science (PCB) record. Recognition and licensing in India or any other country should always be verified with the relevant authority (such as the NMC) — Harwell helps you understand these requirements transparently before you apply.",
    ],
    bullets: [
      "English-taught General Medicine (typically 6 years)",
      "Dentistry and Pharmacy pathways available",
      "Indicative tuition ~€6,000–€8,000 per year",
      "European clinical training environment",
      "Transparent guidance on recognition requirements",
    ],
    programFilter: { fields: ["Medicine"] },
    programsTitle: "MEDICAL PROGRAMS YOU CAN EXPLORE",
    faqKeywords: ["Medicine", "cost", "IELTS", "requirements"],
    showUniversities: true,
  },
  engineering: {
    path: "/engineering-in-romania",
    slug: "engineering-in-romania",
    metaTitle: "Engineering in Romania | CS, AI & Tech Programs | Harwell Global Education",
    metaDesc: "Study Engineering in Romania — Computer Science, AI, Mechanical and Aerospace programs in English at technical universities like POLITEHNICA Bucharest. Guided by Harwell Global Education.",
    overline: "Engineering & Technology",
    h1a: "ENGINEERING IN",
    h1b: "ROMANIA.",
    sub: "Build your future in Engineering, IT, Computer Science and emerging technologies at European technical universities.",
    image: "https://images.unsplash.com/photo-1577985043696-8bd54d9f093f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMHVuaXZlcnNpdHklMjBjYW1wdXMlMjBldXJvcGV8ZW58MHx8fHwxNzg3OTkzMDkzfDA&ixlib=rb-4.1.0&q=85",
    introTitle: "Where European tech education meets real value",
    intro: [
      "Romania is one of Europe's fastest-growing technology ecosystems, and its technical universities — led by names like POLITEHNICA University of Bucharest — offer English-taught programs in Computer Science, AI, Mechanical, Aerospace and Applied Engineering.",
      "Bucharest and Cluj-Napoca host major international tech employers, giving students exposure to a genuine European tech environment while they study.",
    ],
    bullets: [
      "Computer Science, AI, Data Science & Engineering",
      "English-taught Bachelor's and Master's tracks",
      "Indicative tuition ~€2,500–€4,200 per year",
      "Vibrant tech cities: Bucharest & Cluj-Napoca",
      "ECTS-recognised European degrees",
    ],
    programFilter: { fields: ["Engineering", "Computer Science", "Data Science"] },
    programsTitle: "ENGINEERING & TECH PROGRAMS YOU CAN EXPLORE",
    faqKeywords: ["requirements", "IELTS", "early", "cost"],
    showUniversities: true,
  },
  universities: {
    path: "/universities-in-romania",
    slug: "universities-in-romania",
    metaTitle: "Universities in Romania for International Students | Harwell Global Education",
    metaDesc: "Explore leading universities in Romania for international students — University of Bucharest, POLITEHNICA, Babeș-Bolyai, Carol Davila and more. Free profile matching by Harwell.",
    overline: "University Destinations",
    h1a: "UNIVERSITIES IN",
    h1b: "ROMANIA.",
    sub: "Discover universities and programs matched to your academic profile and budget — from Bucharest to Cluj-Napoca, Iași and Timișoara.",
    image: "https://images.unsplash.com/photo-1689655451590-be38de8003e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxyb21hbmlhJTIwYnVjaGFyZXN0JTIwYXJjaGl0ZWN0dXJlJTIwZXVyb3BlfGVufDB8fHx8MTc4Nzk5MzA5M3ww&ixlib=rb-4.1.0&q=85",
    introTitle: "Historic universities, modern English-taught programs",
    intro: [
      "Romania's university landscape combines centuries-old institutions with growing English-taught catalogues for international students — from Alexandru Ioan Cuza University (est. 1860) to the technical strength of POLITEHNICA Bucharest.",
      "Harwell helps students explore these institutions and identifies where their profile, budget and goals fit best. Listings reflect universities we help students explore and do not imply official partnership.",
    ],
    bullets: [
      "University of Bucharest — English-taught pathways",
      "POLITEHNICA Bucharest — programs in foreign languages",
      "Babeș-Bolyai, Cluj-Napoca — large international community",
      "Carol Davila — flagship medical university",
      "Iași & Timișoara — historic, affordable student cities",
    ],
    programFilter: null,
    programsTitle: "PROGRAMS ACROSS ROMANIAN UNIVERSITIES",
    faqKeywords: ["Romania", "requirements", "accommodation", "early"],
    showUniversities: true,
  },
};

function ProgramRows({ filter }) {
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    api.get("/content/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);
  const list = programs.filter((p) => {
    if (!filter) return true;
    if (filter.degree && p.degree !== filter.degree) return false;
    if (filter.fields && !filter.fields.includes(p.field)) return false;
    return true;
  });
  if (list.length === 0) return null;
  return (
    <div className="space-y-4">
      {list.map((p) => (
        <div key={p.id} data-testid={`seo-program-${p.id}`}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 px-6 py-5 hover:border-[#1E40AF]/40 hover:shadow-md transition-all">
          <div>
            <p className="font-bold text-[#0B192C]">{p.name}</p>
            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
              {p.university} <span className="text-slate-300">•</span> <MapPin className="w-3 h-3" /> {p.location}
            </p>
          </div>
          <div className="text-left sm:text-right shrink-0">
            <p className="text-xs font-data text-slate-500">{p.degree} • {p.duration} • {p.language}</p>
            {p.tuition_range && <p className="text-xs font-data text-[#92400E] mt-0.5">{p.tuition_range}</p>}
          </div>
        </div>
      ))}
      <p className="text-xs text-slate-400 text-center pt-2">
        Tuition is indicative and subject to each university's latest official information.
      </p>
    </div>
  );
}

function FaqSubset({ keywords, slug }) {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/content/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);
  const list = faqs.filter((f) => keywords.some((k) => f.question.toLowerCase().includes(k.toLowerCase()))).slice(0, 5);
  if (list.length === 0) return null;
  return (
    <Accordion type="single" collapsible className="space-y-3" data-testid={`${slug}-faq-accordion`}>
      {list.map((f, i) => (
        <AccordionItem key={f.id} value={f.id} data-testid={`${slug}-faq-item-${i}`}
          className="bg-white border border-slate-200 rounded-xl px-6 data-[state=open]:border-[#1E40AF]/40 data-[state=open]:shadow-lg transition-all">
          <AccordionTrigger className="text-left text-[15px] font-bold text-[#0B192C] py-5 hover:no-underline">{f.question}</AccordionTrigger>
          <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-6">{f.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function SeoPage({ config }) {
  useMeta(config.metaTitle, config.metaDesc);
  const navigate = useNavigate();

  return (
    <>
      <section data-testid={`seo-page-${config.slug}`} className="relative min-h-[72svh] flex items-center overflow-hidden bg-[#070E1E]">
        <img src={config.image} alt={config.overline} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070E1E]/95 via-[#070E1E]/80 to-[#070E1E]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070E1E] via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24 w-full">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xs font-data uppercase tracking-[0.25em] text-[#38BDF8] font-semibold mb-5">
            {config.overline}
          </motion.p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] max-w-4xl">
            <span className="block overflow-hidden"><motion.span className="block" initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>{config.h1a}</motion.span></span>
            <span className="block overflow-hidden"><motion.span className="block font-editorial italic font-semibold text-[#F59E0B]" initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}>{config.h1b}</motion.span></span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            {config.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.75 }}
            className="mt-9 flex flex-wrap gap-4">
            <button onClick={() => navigate("/study-in-romania#assessment")} data-testid={`${config.slug}-eligibility-btn`}
              className="px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#2563EB] shadow-xl shadow-blue-950/40 active:scale-[0.98] transition-all flex items-center gap-2">
              CHECK MY ELIGIBILITY <ArrowRight className="w-4 h-4" />
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: config.slug })}
              data-testid={`${config.slug}-whatsapp-btn`}
              className="px-8 py-4 rounded-full text-sm font-bold text-white bg-[#059669] hover:bg-[#047857] active:scale-[0.98] transition-all">
              WHATSAPP US
            </a>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0B192C]">{config.introTitle}</h2>
            {config.intro.map((p, i) => (
              <p key={i} className="mt-5 text-slate-600 leading-relaxed">{p}</p>
            ))}
            <Link to="/study-in-romania" data-testid={`${config.slug}-romania-link`}
              className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#1E40AF] hover:text-[#1D4ED8]">
              EXPLORE STUDY IN ROMANIA 2026 <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.12} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-5">At a Glance</p>
            <ul className="space-y-4">
              {config.bullets.map((b) => (
                <li key={b} className="flex gap-3 text-sm text-slate-700">
                  <span className="w-5 h-5 rounded-full bg-[#E0F2FE] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#1E40AF]" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Programs" title={config.programsTitle} />
          <ProgramRows filter={config.programFilter} />
        </div>
      </section>

      {config.showUniversities && (
        <div className="py-24 bg-[#F1F5F9]">
          <Universities />
        </div>
      )}

      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="FAQ" title="COMMON QUESTIONS" />
          <FaqSubset keywords={config.faqKeywords} slug={config.slug} />
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="bg-[#0B192C] rounded-3xl px-8 py-14 text-center border border-[#1E355B]">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
              READY TO START YOUR <span className="font-editorial italic font-semibold text-[#F59E0B]">EUROPE JOURNEY?</span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">Tell us your academic profile. We'll help you explore the right study options.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <button onClick={() => navigate("/#contact")} data-testid={`${config.slug}-cta-counselling`}
                className="px-8 py-4 rounded-full text-sm font-bold text-[#0B192C] bg-white hover:bg-slate-100 active:scale-[0.98] transition-all">
                GET FREE COUNSELLING
              </button>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: `${config.slug}_cta` })}
                data-testid={`${config.slug}-cta-whatsapp`}
                className="px-8 py-4 rounded-full text-sm font-bold text-white bg-[#059669] hover:bg-[#047857] active:scale-[0.98] transition-all">
                WHATSAPP US
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export function ProgramsPage() {
  useMeta("Study Abroad Programs in Europe & Romania | Harwell Global Education", "Search English-taught Bachelor's and Master's programs in Romania and Europe by field, level and budget. Free profile matching by Harwell Global Education.");
  return (
    <div className="pt-24" data-testid="programs-page">
      <ProgramExplorer />
    </div>
  );
}

export function FaqPage() {
  useMeta("FAQs — Study in Romania & Europe | Harwell Global Education", "Answers to common questions about studying in Romania and Europe: admissions, IELTS, costs, documents, visas and accommodation.");
  return (
    <div className="pt-16" data-testid="faq-page">
      <FAQ standalone />
    </div>
  );
}

export function ContactPage() {
  useMeta("Contact Harwell Global Education | Free Study Abroad Counselling", "Book a free counselling session with Harwell Global Education, Noida. Call, WhatsApp or send an enquiry about studying in Romania and Europe.");
  return (
    <div className="pt-16" data-testid="contact-page">
      <Contact />
    </div>
  );
}

export function AboutPage() {
  useMeta("About Harwell Global Education | European Education Consultancy, Noida", "Harwell Global Education is a Europe-focused international education consultancy from Noida, specialising in Study in Romania for Bachelor's and Master's students.");
  const values = [
    { title: "Transparency First", desc: "No exaggerated promises, no invented statistics. Families get clear, honest information about universities, costs and requirements." },
    { title: "Student-Centred Guidance", desc: "Every shortlist is built around the student's academic profile, interests and budget — never around commissions or quotas." },
    { title: "End-to-End Support", desc: "From the first counselling session to pre-departure preparation, students are guided at every step of the journey." },
    { title: "Credibility by Design", desc: "We present tuition, requirements and outcomes as subject to official university information — because trust is our brand." },
  ];
  return (
    <div data-testid="about-page">
      <section className="bg-[#F8FAFC] mesh-gradient border-b border-slate-200 pt-40 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-data uppercase tracking-[0.25em] text-[#1E40AF] font-semibold mb-5">About Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0B192C] leading-[1.05] max-w-4xl">
            YOUR GLOBAL EDUCATION JOURNEY, <span className="font-editorial italic font-semibold text-[#B45309]">GUIDED WITH CLARITY.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Harwell Global Education is the international education vertical of Harwell Global — helping Indian students
            and families make informed study-abroad decisions across the UK, Germany, Italy, Romania, France and Ireland.
          </p>
        </div>
      </section>
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="What We Stand For" title="BUILT ON TRUST, NOT PROMISES" />
          <div className="grid sm:grid-cols-2 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={(i % 2) * 0.08} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                <p className="font-data text-sm text-[#D97706] font-semibold tracking-widest">0{i + 1}</p>
                <h3 className="mt-3 text-xl font-bold text-[#0B192C]">{v.title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-14">
            <Link to="/contact" data-testid="about-contact-btn"
              className="inline-block px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
              SPEAK TO A COUNSELLOR
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
